import { useState, useEffect } from "react";
import {
    getAllSubscriptions,
    createSubscription,
    updateSubscription,
    deleteSubscription,
    cancelSubscription,
    extendSubscription,
    getAllUsers,
    getAllSubscriptionTypes
} from "../../services/AdminService";
import { PlusIcon, PencilIcon, TrashIcon, BanIcon, ClockIcon, ChevronUpIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/outline";

const SubscriptionModal = ({ isOpen, onClose, onSubmit, title, isEdit = false, formData, handleChange, users, subscriptionTypes }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold" style={{ fontFamily: 'Red Hat Display' }}>{title}</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <form onSubmit={onSubmit} className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium">User</label>
                        <select
                            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            name="user_id"
                            value={formData.user_id || ''}
                            onChange={handleChange}
                            required
                            disabled={isEdit}
                        >
                            <option value="">Select User</option>
                            {users.map((user) => (
                                <option key={user._id?.$oid || user._id} value={user._id?.$oid || user._id}>
                                    {user.firstname} {user.lastname} ({user.email})
                                </option>
                            ))}
                        </select>
                        {isEdit && (
                            <p className="text-xs text-gray-500">User cannot be changed for existing subscriptions</p>
                        )}
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium">Subscription Type</label>
                        <select
                            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            name="subscription_type_id"
                            value={formData.subscription_type_id || ''}
                            onChange={handleChange}
                            required
                        >
                            <option value="">Select Subscription Type</option>
                            {subscriptionTypes.map((type) => (
                                <option key={type._id} value={type._id}>
                                    {type.subscription_name} - ${type.price} {type.currency}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex flex-row gap-4">
                        <div className="flex flex-col w-1/2 gap-2">
                            <label className="text-sm font-medium">Start Date & Time</label>
                            <input
                                className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                type="datetime-local"
                                name="start_date"
                                value={formData.start_date || ''}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="flex flex-col w-1/2 gap-2">
                            <label className="text-sm font-medium">End Date & Time</label>
                            <input
                                className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                type="datetime-local"
                                name="end_date"
                                value={formData.end_date || ''}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div className="flex flex-row gap-4">
                        <div className="flex flex-col w-1/2 gap-2">
                            <label className="text-sm font-medium">Status</label>
                            <select
                                className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                name="status"
                                value={formData.status || ''}
                                onChange={handleChange}
                            >
                                <option value="">Select Status</option>
                                <option value="active">Active</option>
                                <option value="expired">Expired</option>
                                <option value="cancelled">Cancelled</option>
                                <option value="suspended">Suspended</option>
                            </select>
                        </div>
                        <div className="flex flex-col w-1/2 gap-2">
                            <label className="text-sm font-medium">Payment ID</label>
                            <input
                                className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                type="text"
                                placeholder="admin_created_12345"
                                name="payment_id"
                                value={formData.payment_id || ''}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 mt-6">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 text-sm text-white rounded-md bg-orange hover:bg-opacity-90 transition-colors duration-200"
                        >
                            {isEdit ? 'Update' : 'Create'} Subscription
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const ExtendModal = ({ isOpen, onClose, onSubmit, subscription }) => {
    const [days, setDays] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        if (days && parseInt(days) > 0) {
            onSubmit(parseInt(days));
            setDays('');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold" style={{ fontFamily: 'Red Hat Display' }}>Extend Subscription</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <div className="bg-gray-50 p-4 rounded-md">
                        <p className="text-sm text-gray-600">
                            <strong>Current End Date (SGT):</strong> {subscription?.end_date ? new Date(subscription.end_date).toLocaleString('en-US', { timeZone: 'Asia/Singapore' }) : 'N/A'}
                        </p>
                        <p className="text-sm text-gray-600">
                            <strong>User:</strong> {subscription?.user_email}
                        </p>
                        <p className="text-sm text-gray-600">
                            <strong>Plan:</strong> {subscription?.subscription_name}
                        </p>
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium">Extend by (days)</label>
                        <input
                            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            type="number"
                            placeholder="30"
                            value={days}
                            onChange={(e) => setDays(e.target.value)}
                            min="1"
                            required
                        />
                    </div>

                    <div className="flex justify-end gap-2 mt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 text-sm text-white rounded-md bg-blue-600 hover:bg-blue-700 transition-colors duration-200"
                        >
                            Extend Subscription
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const SubscriptionManagementPage = () => {
    const [subscriptions, setSubscriptions] = useState([]);
    const [users, setUsers] = useState([]);
    const [subscriptionTypes, setSubscriptionTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showExtendModal, setShowExtendModal] = useState(false);
    const [selectedSubscription, setSelectedSubscription] = useState(null);

    // Helper function to convert to SGT
    const toSGT = (date) => {
        if (!date) return null;
        return new Date(date).toLocaleString('en-US', {
            timeZone: 'Asia/Singapore',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
    };

    // Helper function to convert datetime-local input to SGT timezone format
    const formatToSGTTimezone = (datetimeLocal) => {
        if (!datetimeLocal) return null;
        // datetime-local format: "2025-06-30T23:59"
        // We treat this as SGT time and add the timezone offset
        return `${datetimeLocal}:00+08:00`;
    };


    // Helper function to calculate remaining time
    const calculateRemainingTime = (startDate, endDate) => {
        if (!endDate) return 'N/A';

        const now = new Date();
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffMs = end - now;

        if ((start >= now)) return 'Not yet started';

        if (diffMs <= 0) return 'Expired';

        const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

        let result = [];
        if (days > 0) result.push(`${days} day${days !== 1 ? 's' : ''}`);
        if (hours > 0) result.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
        if (minutes > 0) result.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);

        return result.length > 0 ? result.join(' ') : 'Less than 1 minute';
    };

    // Helper function to check and update expired subscriptions
    const checkAndUpdateExpiredStatus = (subscriptionsList) => {
        const now = new Date();
        return subscriptionsList.map(subscription => {
            if (subscription.end_date && subscription.status === 'active') {
                const endDate = new Date(subscription.end_date);
                if (endDate <= now) {
                    return { ...subscription, status: 'expired' };
                }
            }
            return subscription;
        });
    };

    // Search and Filter states
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [userFilter, setUserFilter] = useState("all");

    // Sorting states
    const [sortField, setSortField] = useState("created_at");
    const [sortDirection, setSortDirection] = useState("desc");

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const [formData, setFormData] = useState({
        user_id: '',
        subscription_type_id: '',
        start_date: '',
        end_date: '',
        status: '',
        payment_id: ''
    });

    useEffect(() => {
        fetchInitialData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            const [subscriptionsRes, usersRes, typesRes] = await Promise.all([
                getAllSubscriptions(),
                getAllUsers(),
                getAllSubscriptionTypes()
            ]);

            if (subscriptionsRes?.Data?.subscriptions) {
                // Check and update expired subscriptions
                const updatedSubscriptions = checkAndUpdateExpiredStatus(subscriptionsRes.Data.subscriptions);
                setSubscriptions(updatedSubscriptions);
            }

            if (usersRes?.Data) {
                setUsers(usersRes.Data);
            }

            if (typesRes?.Data) {
                setSubscriptionTypes(typesRes.Data);
            }
        } catch (error) {
            console.error("Error fetching initial data:", error);
            setError(error.message || "An error occurred while fetching data");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateSubscription = async (e) => {
        e.preventDefault();
        try {
            const submitData = { ...formData };

            // Convert datetime-local inputs to SGT timezone format
            if (submitData.start_date) {
                submitData.start_date = formatToSGTTimezone(submitData.start_date);
            }
            if (submitData.end_date) {
                submitData.end_date = formatToSGTTimezone(submitData.end_date);
            }

            // Remove empty fields
            Object.keys(submitData).forEach(key => {
                if (!submitData[key]) {
                    delete submitData[key];
                }
            });

            const response = await createSubscription(submitData);
            if (response.status_code === 200) {
                alert(response.message);
                setShowCreateModal(false);
                resetForm();
                fetchInitialData();
            } else {
                alert(response.message || 'Failed to create subscription');
            }
        } catch (error) {
            alert(error.response?.data?.message || error.message || 'Failed to create subscription');
        }
    };

    const handleUpdateSubscription = async (e) => {
        e.preventDefault();
        try {
            const submitData = { ...formData };

            // Convert datetime-local inputs to SGT timezone format
            if (submitData.start_date) {
                submitData.start_date = formatToSGTTimezone(submitData.start_date);
            }
            if (submitData.end_date) {
                submitData.end_date = formatToSGTTimezone(submitData.end_date);
            }

            // Remove empty fields and user_id (can't change user)
            Object.keys(submitData).forEach(key => {
                if (!submitData[key] || key === 'user_id') {
                    delete submitData[key];
                }
            });

            const response = await updateSubscription(selectedSubscription._id, submitData);
            console.log(submitData)
            if (response["Base Output"]?.status_code === 200) {
                alert(response["Base Output"].message);
                setShowEditModal(false);
                setSelectedSubscription(null);
                resetForm();
                fetchInitialData();
            } else {
                alert(response["Base Output"]?.message || 'Failed to update subscription');
            }
        } catch (error) {
            alert(error.response?.data?.message || error.message || 'Failed to update subscription');
        }
    };

    const handleCancelSubscription = async (subscriptionId) => {
        const reason = prompt('Enter cancellation reason (optional):');
        if (reason !== null) { // User didn't cancel the prompt
            try {
                const response = await cancelSubscription(subscriptionId, reason || undefined);
                if (response.status_code === 200) {
                    alert('Subscription cancelled successfully');
                    fetchInitialData();
                } else {
                    alert(response.message || 'Failed to cancel subscription');
                }
            } catch (error) {
                alert(error.response?.data?.message || error.message || 'Failed to cancel subscription');
            }
        }
    };

    const handleExtendSubscription = async (days) => {
        try {
            const response = await extendSubscription(selectedSubscription._id, days);
            if (response["Base Output"]?.status_code === 200) {
                alert(response["Base Output"].message);
                setShowExtendModal(false);
                setSelectedSubscription(null);
                fetchInitialData();
            } else {
                alert(response["Base Output"]?.message || 'Failed to extend subscription');
            }
        } catch (error) {
            alert(error.response?.data?.message || error.message || 'Failed to extend subscription');
        }
    };

    const handleDeleteSubscription = async (subscriptionId) => {
        if (window.confirm('Are you sure you want to delete this subscription? This action cannot be undone.')) {
            try {
                const response = await deleteSubscription(subscriptionId);
                if (response.status_code === 200) {
                    alert('Subscription deleted successfully');
                    fetchInitialData();
                } else {
                    alert(response.message || 'Failed to delete subscription');
                }
            } catch (error) {
                alert(error.response?.data?.message || error.message || 'Failed to delete subscription');
            }
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const resetForm = () => {
        setFormData({
            user_id: '',
            subscription_type_id: '',
            start_date: '',
            end_date: '',
            status: '',
            payment_id: ''
        });
    };

    const openCreateModal = () => {
        resetForm();
        setShowCreateModal(true);
    };

    const openEditModal = (subscription) => {
        setSelectedSubscription(subscription);

        // Format datetime for datetime-local input (YYYY-MM-DDTHH:MM) in SGT
        const formatDateTimeLocal = (dateString) => {
            if (!dateString) return '';
            const date = new Date(dateString);
            // Convert to SGT timezone
            const sgtDate = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Singapore' }));
            const year = sgtDate.getFullYear();
            const month = String(sgtDate.getMonth() + 1).padStart(2, '0');
            const day = String(sgtDate.getDate()).padStart(2, '0');
            const hours = String(sgtDate.getHours()).padStart(2, '0');
            const minutes = String(sgtDate.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}`;
        };

        setFormData({
            user_id: subscription.user_id,
            subscription_type_id: subscription.subscription_type_id,
            start_date: formatDateTimeLocal(subscription.start_date),
            end_date: formatDateTimeLocal(subscription.end_date),
            status: subscription.status,
            payment_id: subscription.payment_id
        });
        setShowEditModal(true);
    };

    const openExtendModal = (subscription) => {
        setSelectedSubscription(subscription);
        setShowExtendModal(true);
    };

    const closeModals = () => {
        setShowCreateModal(false);
        setShowEditModal(false);
        setShowExtendModal(false);
        setSelectedSubscription(null);
        resetForm();
    };

    // Sorting function
    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
        setCurrentPage(1);
    };

    // Filter and sort subscriptions
    const filteredSubscriptions = subscriptions.filter(subscription => {
        const matchesSearch = subscription.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            subscription.subscription_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            subscription.payment_id?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === "all" || subscription.status === statusFilter;
        const matchesUser = userFilter === "all" || subscription.user_id === userFilter;

        return matchesSearch && matchesStatus && matchesUser;
    });

    const sortedSubscriptions = [...filteredSubscriptions].sort((a, b) => {
        let aValue, bValue;

        switch (sortField) {
            case 'user_email':
                aValue = a.user_email?.toLowerCase() || '';
                bValue = b.user_email?.toLowerCase() || '';
                break;
            case 'subscription_name':
                aValue = a.subscription_name?.toLowerCase() || '';
                bValue = b.subscription_name?.toLowerCase() || '';
                break;
            case 'status':
                aValue = a.status?.toLowerCase() || '';
                bValue = b.status?.toLowerCase() || '';
                break;
            case 'start_date':
                aValue = new Date(a.start_date || 0);
                bValue = new Date(b.start_date || 0);
                break;
            case 'end_date':
                aValue = new Date(a.end_date || 0);
                bValue = new Date(b.end_date || 0);
                break;
            case 'created_at':
            default:
                aValue = new Date(a.created_at || 0);
                bValue = new Date(b.created_at || 0);
                break;
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
    });

    // Pagination
    const totalPages = Math.ceil(sortedSubscriptions.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedSubscriptions = sortedSubscriptions.slice(startIndex, startIndex + itemsPerPage);

    // Pagination controls
    const goToPage = (page) => {
        setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    };

    const SortableHeader = ({ field, children, align = 'left', minWidth = 'auto' }) => (
        <th
            className={`px-3 py-3 text-${align} text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors duration-200`}
            onClick={() => handleSort(field)}
            style={{ minWidth }}
        >
            <div className="flex items-center space-x-1">
                <span className="truncate">{children}</span>
                <div className="flex flex-col flex-shrink-0">
                    <ChevronUpIcon
                        className={`w-3 h-3 ${sortField === field && sortDirection === 'asc' ? 'text-orange-500' : 'text-gray-400'}`}
                    />
                    <ChevronDownIcon
                        className={`w-3 h-3 -mt-1 ${sortField === field && sortDirection === 'desc' ? 'text-orange-500' : 'text-gray-400'}`}
                    />
                </div>
            </div>
        </th>
    );

    return (
        <div className="mb-8">
            <div className="flex justify-between items-center mb-6">
                <button
                    onClick={openCreateModal}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-white rounded-md bg-orange hover:bg-opacity-90 transition-colors duration-200"
                >
                    <PlusIcon className="w-4 h-4" />
                    Create Subscription
                </button>
            </div>

            {/* Filters */}
            <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">Search</label>
                    <input
                        type="text"
                        placeholder="Search by user, plan, or payment ID..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                </div>

                <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">Status</label>
                    <select
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="expired">Expired</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="suspended">Suspended</option>
                    </select>
                </div>

                <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">User</label>
                    <select
                        value={userFilter}
                        onChange={(e) => {
                            setUserFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                        <option value="all">All Users</option>
                        {users.map((user) => (
                            <option key={user._id?.$oid || user._id} value={user._id?.$oid || user._id}>
                                {user.firstname} {user.lastname}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">Items per page</label>
                    <select
                        value={itemsPerPage}
                        onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                    </select>
                </div>
            </div>

            {/* Subscriptions Table */}
            {loading ? (
                <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
                </div>
            ) : error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-700">Error: {error}</p>
                </div>
            ) : (
                <>
                    {/* Data Count Info */}
                    <div className="mb-4 flex items-center justify-between bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">
                        <div className="flex items-center space-x-4">
                            <div className="text-sm text-gray-700">
                                <span className="font-medium text-gray-900">{filteredSubscriptions.length}</span>
                                {filteredSubscriptions.length === 1 ? ' subscription' : ' subscriptions'} found
                                {(searchTerm || statusFilter !== 'all' || userFilter !== 'all') && (
                                    <span className="text-gray-500"> (filtered from {subscriptions.length} total)</span>
                                )}
                            </div>
                            {(searchTerm || statusFilter !== 'all' || userFilter !== 'all') && (
                                <div className="flex items-center space-x-2">
                                    {searchTerm && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                            Search: "{searchTerm}"
                                        </span>
                                    )}
                                    {statusFilter !== 'all' && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                            Status: {statusFilter}
                                        </span>
                                    )}
                                    {userFilter !== 'all' && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            User: {users.find(u => (u._id?.$oid || u._id) === userFilter)?.firstname} {users.find(u => (u._id?.$oid || u._id) === userFilter)?.lastname}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                        {(searchTerm || statusFilter !== 'all' || userFilter !== 'all') && (
                            <button
                                onClick={() => {
                                    setSearchTerm('');
                                    setStatusFilter('all');
                                    setUserFilter('all');
                                    setCurrentPage(1);
                                }}
                                className="text-sm text-gray-500 hover:text-gray-700 underline"
                            >
                                Clear all filters
                            </button>
                        )}
                    </div>

                    <div className="bg-white shadow-lg rounded-lg overflow-hidden border border-gray-200">
                        <div className="overflow-x-auto">
                            <table className="w-full divide-y divide-gray-200" style={{ minWidth: '1200px' }}>
                                <thead className="bg-gray-50">
                                    <tr>
                                        <SortableHeader field="user_email" minWidth="200px">User</SortableHeader>
                                        <SortableHeader field="subscription_name" minWidth="150px">Plan</SortableHeader>
                                        <SortableHeader field="status" minWidth="100px">Status</SortableHeader>
                                        <SortableHeader field="start_date" minWidth="140px">Start (SGT)</SortableHeader>
                                        <SortableHeader field="end_date" minWidth="140px">End (SGT)</SortableHeader>
                                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '140px' }}>
                                            <span className="truncate">Remaining</span>
                                        </th>
                                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '120px' }}>
                                            <span className="truncate">Payment</span>
                                        </th>
                                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '120px' }}>
                                            <span className="truncate">Actions</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {paginatedSubscriptions.length > 0 ? (
                                        paginatedSubscriptions.map((subscription, index) => (
                                            <tr key={subscription._id || `subscription-${index}`} className="hover:bg-gray-50 transition-colors duration-200">
                                                <td className="px-3 py-3" style={{ minWidth: '200px' }}>
                                                    <div className="flex flex-col">
                                                        <div className="text-sm font-medium text-gray-900 truncate" title={subscription.user_email}>
                                                            {subscription.user_email}
                                                        </div>
                                                        <div className="text-xs text-gray-500 truncate" title={subscription.user_id}>
                                                            ID: {subscription.user_id}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-3 py-3" style={{ minWidth: '150px' }}>
                                                    <div className="text-sm font-medium text-gray-900 truncate" title={subscription.subscription_name}>
                                                        {subscription.subscription_name}
                                                    </div>
                                                </td>
                                                <td className="px-3 py-3" style={{ minWidth: '100px' }}>
                                                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${subscription.status === 'active' ? 'bg-green-100 text-green-800' :
                                                        subscription.status === 'expired' ? 'bg-red-100 text-red-800' :
                                                            subscription.status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                                                                subscription.status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                                                                    'bg-purple-100 text-purple-800'
                                                        }`}>
                                                        {subscription.status?.charAt(0).toUpperCase() + subscription.status?.slice(1)}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-3 text-xs text-gray-900" style={{ minWidth: '140px' }}>
                                                    <div title={subscription.start_date ? toSGT(subscription.start_date) : 'N/A'}>
                                                        {subscription.start_date ? toSGT(subscription.start_date) : 'N/A'}
                                                    </div>
                                                </td>
                                                <td className="px-3 py-3 text-xs text-gray-900" style={{ minWidth: '140px' }}>
                                                    <div title={subscription.end_date ? toSGT(subscription.end_date) : 'N/A'}>
                                                        {subscription.end_date ? toSGT(subscription.end_date) : 'N/A'}
                                                    </div>
                                                </td>
                                                <td className="px-3 py-3" style={{ minWidth: '140px' }}>
                                                    <span className={`px-2 py-1 rounded text-xs font-medium block ${calculateRemainingTime(subscription.start_date, subscription.end_date) === 'Expired'
                                                        ? 'bg-red-100 text-red-800'
                                                        : calculateRemainingTime(subscription.start_date, subscription.end_date) === 'Not yet started'
                                                            ? 'bg-yellow-100 text-gray-800'
                                                            : calculateRemainingTime(subscription.start_date, subscription.end_date) === 'N/A'
                                                                ? 'bg-gray-100 text-gray-800'
                                                                : 'bg-blue-100 text-blue-800'
                                                        }`} title={calculateRemainingTime(subscription.start_date, subscription.end_date)}>
                                                        {calculateRemainingTime(subscription.start_date, subscription.end_date)}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-3" style={{ minWidth: '120px' }}>
                                                    <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded block truncate" title={subscription.payment_id || 'N/A'}>
                                                        {subscription.payment_id || 'N/A'}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-3" style={{ minWidth: '120px' }}>
                                                    <div className="flex items-center space-x-1">
                                                        <button
                                                            onClick={() => openEditModal(subscription)}
                                                            className="text-orange-600 hover:text-orange-900 transition-colors duration-200"
                                                            title="Edit Subscription"
                                                        >
                                                            <PencilIcon className="w-4 h-4" />
                                                        </button>
                                                        {subscription.status === 'active' && (
                                                            <>
                                                                <button
                                                                    onClick={() => openExtendModal(subscription)}
                                                                    className="text-blue-600 hover:text-blue-900 transition-colors duration-200"
                                                                    title="Extend Subscription"
                                                                >
                                                                    <ClockIcon className="w-4 h-4" />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleCancelSubscription(subscription._id)}
                                                                    className="text-yellow-600 hover:text-yellow-900 transition-colors duration-200"
                                                                    title="Cancel Subscription"
                                                                >
                                                                    <BanIcon className="w-4 h-4" />
                                                                </button>
                                                            </>
                                                        )}
                                                        <button
                                                            onClick={() => handleDeleteSubscription(subscription._id)}
                                                            className="text-red-600 hover:text-red-900 transition-colors duration-200"
                                                            title="Delete Subscription"
                                                        >
                                                            <TrashIcon className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                                                <div className="flex flex-col items-center">
                                                    <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                    <p className="text-lg font-medium">No subscriptions found</p>
                                                    <p className="text-sm">Try adjusting your search or filter criteria</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4 rounded-lg shadow">
                            <div className="flex-1 flex justify-between sm:hidden">
                                <button
                                    onClick={() => goToPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => goToPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                <div>
                                    <p className="text-sm text-gray-700">
                                        Showing{' '}
                                        <span className="font-medium">{startIndex + 1}</span>
                                        {' '}to{' '}
                                        <span className="font-medium">
                                            {Math.min(startIndex + itemsPerPage, sortedSubscriptions.length)}
                                        </span>
                                        {' '}of{' '}
                                        <span className="font-medium">{sortedSubscriptions.length}</span>
                                        {' '}results
                                    </p>
                                </div>
                                <div>
                                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                        <button
                                            onClick={() => goToPage(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <ChevronLeftIcon className="h-5 w-5" />
                                        </button>

                                        {/* Page numbers */}
                                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                            let pageNum;

                                            if (totalPages <= 5) {
                                                pageNum = i + 1;
                                            } else if (currentPage <= 3) {
                                                pageNum = i + 1;
                                            } else if (currentPage >= totalPages - 2) {
                                                pageNum = totalPages - 4 + i;
                                            } else {
                                                pageNum = currentPage - 2 + i;
                                            }

                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => goToPage(pageNum)}
                                                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === pageNum
                                                        ? 'z-10 bg-orange-500 border-orange-500 text-white'
                                                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                                        }`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}

                                        <button
                                            onClick={() => goToPage(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <ChevronRightIcon className="h-5 w-5" />
                                        </button>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Create Subscription Modal */}
            <SubscriptionModal
                isOpen={showCreateModal}
                onClose={closeModals}
                onSubmit={handleCreateSubscription}
                title="Create New Subscription"
                formData={formData}
                handleChange={handleChange}
                users={users}
                subscriptionTypes={subscriptionTypes}
            />

            {/* Edit Subscription Modal */}
            <SubscriptionModal
                isOpen={showEditModal}
                onClose={closeModals}
                onSubmit={handleUpdateSubscription}
                title="Edit Subscription"
                isEdit={true}
                formData={formData}
                handleChange={handleChange}
                users={users}
                subscriptionTypes={subscriptionTypes}
            />

            {/* Extend Subscription Modal */}
            <ExtendModal
                isOpen={showExtendModal}
                onClose={closeModals}
                onSubmit={handleExtendSubscription}
                subscription={selectedSubscription}
            />
        </div>
    );
};

export default SubscriptionManagementPage;